import { mapFarms } from '../utils/mapMockData';
import { MOCK_USERS } from '../utils/mockUsersData';
import { STORAGE_KEYS, readStorageValue, writeStorageValue } from '../utils/storageKeys';

const USERS_STORAGE_VERSION = 1;
const USER_ROLES = new Set(['superadmin', 'admin', 'reviewer', 'viewer', 'custom']);
const AVAILABLE_FARM_IDS = new Set(mapFarms.map((farm) => farm.id));
const DEFAULT_DELAY_MS = 120;

let cachedEnvelope = null;

const delay = (ms = DEFAULT_DELAY_MS) => new Promise((resolve) => {
  globalThis.setTimeout(resolve, ms);
});

const getStorage = () => (
  typeof window !== 'undefined' && window.localStorage ? window.localStorage : null
);

const cloneValue = (value) => JSON.parse(JSON.stringify(value));

const toIsoString = (value, fallback = null) => {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toISOString();
};

const normalizePermissions = (role, customPermissions) => {
  if (role !== 'custom') {
    return null;
  }

  const pages = Array.isArray(customPermissions?.pages)
    ? customPermissions.pages.map((page) => String(page).trim()).filter(Boolean)
    : [];

  return {
    pages: Array.from(new Set(pages)),
    analyticsPermissions: {
      downloadData: customPermissions?.analyticsPermissions?.downloadData === true,
    },
    reportsPermissions: {
      viewReports: customPermissions?.reportsPermissions?.viewReports === true,
      downloadDocuments: customPermissions?.reportsPermissions?.downloadDocuments === true,
    },
  };
};

const normalizeFarms = (farms) => {
  const source = Array.isArray(farms) ? farms : [];
  const normalized = source
    .map((farmId) => String(farmId).trim())
    .filter((farmId) => AVAILABLE_FARM_IDS.has(farmId));

  return Array.from(new Set(normalized));
};

const normalizeStoredUser = (user, fallbackId) => {
  const nowIso = new Date().toISOString();
  const role = USER_ROLES.has(user?.role) ? user.role : 'viewer';
  const createdAt = toIsoString(user?.createdAt, nowIso);
  const updatedAt = toIsoString(user?.updatedAt, createdAt);

  return {
    id: String(user?.id || fallbackId),
    email: String(user?.email || '').trim().toLowerCase(),
    firstName: String(user?.firstName || '').trim(),
    lastName: String(user?.lastName || '').trim(),
    phone: user?.phone == null ? '' : String(user.phone).trim(),
    role,
    farms: normalizeFarms(user?.farms),
    customPermissions: normalizePermissions(role, user?.customPermissions),
    isActive: user?.isActive !== false,
    emailVerified: user?.emailVerified === true,
    lastLogin: toIsoString(user?.lastLogin),
    passwordUpdatedAt: toIsoString(user?.passwordUpdatedAt),
    createdAt,
    updatedAt,
  };
};

const createSeedEnvelope = () => ({
  version: USERS_STORAGE_VERSION,
  updatedAt: new Date().toISOString(),
  users: MOCK_USERS.map((user, index) => normalizeStoredUser(user, `seed-user-${index + 1}`)),
});

const getEnvelopeFromStorage = () => {
  if (cachedEnvelope) {
    return cloneValue(cachedEnvelope);
  }

  const storage = getStorage();
  if (!storage) {
    cachedEnvelope = createSeedEnvelope();
    return cloneValue(cachedEnvelope);
  }

  const storedEntry = readStorageValue(storage, STORAGE_KEYS.demoUsers);
  if (!storedEntry?.value) {
    const seededEnvelope = createSeedEnvelope();
    writeStorageValue(storage, STORAGE_KEYS.demoUsers, JSON.stringify(seededEnvelope));
    cachedEnvelope = seededEnvelope;
    return cloneValue(seededEnvelope);
  }

  try {
    const parsed = JSON.parse(storedEntry.value);
    const users = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.users)
        ? parsed.users
        : null;

    if (!users) {
      throw new Error('Invalid demo users payload');
    }

    const envelope = {
      version: USERS_STORAGE_VERSION,
      updatedAt: toIsoString(parsed?.updatedAt, new Date().toISOString()),
      users: users.map((user, index) => normalizeStoredUser(user, `stored-user-${index + 1}`)),
    };

    cachedEnvelope = envelope;
    writeStorageValue(storage, STORAGE_KEYS.demoUsers, JSON.stringify(envelope));
    return cloneValue(envelope);
  } catch (error) {
    const seededEnvelope = createSeedEnvelope();
    writeStorageValue(storage, STORAGE_KEYS.demoUsers, JSON.stringify(seededEnvelope));
    cachedEnvelope = seededEnvelope;
    return cloneValue(seededEnvelope);
  }
};

const persistUsers = (users) => {
  const envelope = {
    version: USERS_STORAGE_VERSION,
    updatedAt: new Date().toISOString(),
    users: users.map((user, index) => normalizeStoredUser(user, `persisted-user-${index + 1}`)),
  };

  cachedEnvelope = envelope;

  const storage = getStorage();
  if (storage) {
    writeStorageValue(storage, STORAGE_KEYS.demoUsers, JSON.stringify(envelope));
  }

  return cloneValue(envelope.users);
};

const getAllUsers = () => getEnvelopeFromStorage().users;

const sortUsers = (users, sortByInput, sortOrderInput) => {
  const sortBy = typeof sortByInput === 'string' && sortByInput.trim() ? sortByInput : 'email';
  const direction = String(sortOrderInput).toLowerCase() === 'desc' ? -1 : 1;
  const valueForSort = (user) => {
    switch (sortBy) {
      case 'firstName':
        return user.firstName.toLowerCase();
      case 'lastName':
        return user.lastName.toLowerCase();
      case 'createdAt':
        return user.createdAt || '';
      case 'updatedAt':
        return user.updatedAt || '';
      case 'role':
        return user.role;
      case 'email':
      default:
        return user.email;
    }
  };

  return [...users].sort((left, right) => {
    const leftValue = valueForSort(left);
    const rightValue = valueForSort(right);

    if (leftValue < rightValue) return -1 * direction;
    if (leftValue > rightValue) return 1 * direction;
    return left.id.localeCompare(right.id) * direction;
  });
};

const validateUser = (payload, existingUsers, options = {}) => {
  const isCreate = options.isCreate === true;
  const existingId = options.existingId || null;
  const email = String(payload?.email || '').trim().toLowerCase();
  const firstName = String(payload?.firstName || '').trim();
  const lastName = String(payload?.lastName || '').trim();
  const role = USER_ROLES.has(payload?.role) ? payload.role : null;
  const farms = normalizeFarms(payload?.farms);
  const password = payload?.password == null ? '' : String(payload.password);

  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error('Please enter a valid email address.');
  }
  if (!firstName) {
    throw new Error('First name is required.');
  }
  if (!lastName) {
    throw new Error('Last name is required.');
  }
  if (!role) {
    throw new Error('Please choose a valid role.');
  }
  if (farms.length === 0) {
    throw new Error('Select at least one farm.');
  }
  if (role === 'custom' && normalizePermissions(role, payload?.customPermissions)?.pages.length === 0) {
    throw new Error('Select at least one page for a custom role.');
  }
  if (isCreate && password.length < 8) {
    throw new Error('Password must be at least 8 characters.');
  }
  if (!isCreate && password && password.length < 8) {
    throw new Error('Password must be at least 8 characters.');
  }

  const duplicate = existingUsers.find((user) => user.email === email && user.id !== existingId);
  if (duplicate) {
    throw new Error('A user with this email already exists.');
  }

  return {
    email,
    firstName,
    lastName,
    phone: payload?.phone == null ? '' : String(payload.phone).trim(),
    role,
    farms,
    customPermissions: normalizePermissions(role, payload?.customPermissions),
    isActive: payload?.isActive !== false,
    emailVerified: payload?.emailVerified === true,
    password,
  };
};

const createUserId = () => `du_local_${Math.random().toString(36).slice(2, 10)}`;

const buildStats = (users) => ({
  totalUsers: users.length,
  activeUsers: users.filter((user) => user.isActive).length,
  adminUsers: users.filter((user) => user.role === 'admin' || user.role === 'superadmin').length,
  reviewerUsers: users.filter((user) => user.role === 'reviewer').length,
  viewerUsers: users.filter((user) => user.role === 'viewer').length,
});

export const usersApi = {
  list: async (params = {}) => {
    await delay();

    const page = Math.max(1, Number.parseInt(String(params.page || '1'), 10) || 1);
    const limit = Math.max(1, Number.parseInt(String(params.limit || '200'), 10) || 200);
    const normalizedSearch = String(params.search || '').trim().toLowerCase();

    let filteredUsers = getAllUsers();

    if (params.role && params.role !== 'all') {
      filteredUsers = filteredUsers.filter((user) => user.role === params.role);
    }
    if (typeof params.isActive === 'boolean') {
      filteredUsers = filteredUsers.filter((user) => user.isActive === params.isActive);
    }
    if (normalizedSearch) {
      filteredUsers = filteredUsers.filter((user) => (
        user.email.toLowerCase().includes(normalizedSearch)
        || user.firstName.toLowerCase().includes(normalizedSearch)
        || user.lastName.toLowerCase().includes(normalizedSearch)
      ));
    }

    const sortedUsers = sortUsers(filteredUsers, params.sortBy, params.sortOrder);
    const total = sortedUsers.length;
    const offset = (page - 1) * limit;
    const items = sortedUsers.slice(offset, offset + limit);

    return {
      items,
      total,
      page,
      limit,
      totalPages: Math.max(1, Math.ceil(total / limit) || 1),
    };
  },

  getById: async (id) => {
    await delay();
    const user = getAllUsers().find((entry) => entry.id === id);
    if (!user) {
      throw new Error('User not found.');
    }
    return cloneValue(user);
  },

  create: async (data) => {
    await delay();

    const existingUsers = getAllUsers();
    const validated = validateUser(data, existingUsers, { isCreate: true });
    const nowIso = new Date().toISOString();

    const nextUsers = persistUsers([
      ...existingUsers,
      {
        id: createUserId(),
        email: validated.email,
        firstName: validated.firstName,
        lastName: validated.lastName,
        phone: validated.phone,
        role: validated.role,
        farms: validated.farms,
        customPermissions: validated.customPermissions,
        isActive: validated.isActive,
        emailVerified: false,
        lastLogin: null,
        passwordUpdatedAt: validated.password ? nowIso : null,
        createdAt: nowIso,
        updatedAt: nowIso,
      },
    ]);

    return cloneValue(nextUsers[nextUsers.length - 1]);
  },

  update: async (id, data) => {
    await delay();

    const existingUsers = getAllUsers();
    const existingUser = existingUsers.find((user) => user.id === id);
    if (!existingUser) {
      throw new Error('User not found.');
    }

    const mergedCandidate = { ...existingUser, ...data };
    const validated = validateUser(mergedCandidate, existingUsers, { existingId: id });
    const nowIso = new Date().toISOString();

    const nextUsers = persistUsers(existingUsers.map((user) => {
      if (user.id !== id) {
        return user;
      }

      return {
        ...user,
        email: validated.email,
        firstName: validated.firstName,
        lastName: validated.lastName,
        phone: validated.phone,
        role: validated.role,
        farms: validated.farms,
        customPermissions: validated.customPermissions,
        isActive: validated.isActive,
        updatedAt: nowIso,
        passwordUpdatedAt: validated.password ? nowIso : user.passwordUpdatedAt,
      };
    }));

    const updatedUser = nextUsers.find((user) => user.id === id);
    return cloneValue(updatedUser);
  },

  delete: async (id) => {
    await delay();

    const existingUsers = getAllUsers();
    const nextUsers = existingUsers.filter((user) => user.id !== id);
    if (nextUsers.length === existingUsers.length) {
      throw new Error('User not found.');
    }

    persistUsers(nextUsers);
    return { success: true };
  },

  getStats: async () => {
    await delay();
    return buildStats(getAllUsers());
  },

  resetPassword: async (id, newPassword) => {
    await delay();

    if (!newPassword || String(newPassword).trim().length < 8) {
      throw new Error('Password must be at least 8 characters.');
    }

    const existingUsers = getAllUsers();
    const nowIso = new Date().toISOString();
    let found = false;

    const nextUsers = persistUsers(existingUsers.map((user) => {
      if (user.id !== id) {
        return user;
      }

      found = true;
      return {
        ...user,
        passwordUpdatedAt: nowIso,
        updatedAt: nowIso,
      };
    }));

    if (!found) {
      throw new Error('User not found.');
    }

    const updatedUser = nextUsers.find((user) => user.id === id);
    return {
      success: true,
      user: cloneValue(updatedUser),
      passwordUpdatedAt: nowIso,
    };
  },
};
